import { Vue, Ref, Prop, Watch } from 'vue-property-decorator';
import Component from 'vue-class-component';
import { BModal } from 'bootstrap-vue/esm/components/modal';
import { Camera } from '@/models/camera';
import { Project } from '@/models/project';
import { User } from '@/models/user';
import { Getter, State, Action } from 'vuex-class';
import { CameraUpdateRequest, CameraDeleteRequest } from '@/services/cameras/cameras.interfaces';
import { Storage } from 'aws-amplify';
import VueSlider from 'vue-slider-component';
// @ts-ignore no d.ts
import Datepicker from 'vuejs-datepicker';
import VueTimepicker from 'vue2-timepicker/src/vue-timepicker.vue';
// @ts-ignore no d.ts
import { en, fr } from 'vuejs-datepicker/dist/locale';
import BatterieLevelComponent from '@/components/batterielevel/batterielevel.component.vue';
import InputDays from '@/components/inputdays/inputdays.component.vue';
import { parse, isPast } from 'date-fns';
import { Validations } from 'vuelidate-property-decorators';
import { Shadow } from '@/models/shadow';
import { Logger } from 'aws-amplify';
import { cameraService } from '@/services';
const logger = new Logger('CameraModal');

// Can merge with timelapsefilter period interface
interface Time {
  HH?: string;
  mm?: string;
}
interface Period {
  begin: Time;
  end: Time;
}

@Component({
  components: {
    BatterieLevelComponent,
    VueSlider,
    VueTimepicker,
    Datepicker,
    'input-days': InputDays,
  },
})
export default class CameraModal extends Vue {
  public lastPhoto = '';

  @Prop({ type: Object, required: true }) private readonly camera!: Camera;

  @Ref('camera-modal') private modal!: BModal;

  @State('projects', { namespace: 'projects' }) private projects!: Project[];
  @State('locale', { namespace: 'profile' }) private locale!: string;
  @State('user', { namespace: 'profile' }) private user!: User;
  //@Prop({ required: true }) readonly isPremium!: boolean;
  @State('loadingCamera', { namespace: 'camera' }) private loadingCamera!: boolean;

  @Action('loadProject', { namespace: 'projects' }) private loadProject!: () => Promise<Project[]>;
  @Action('loadCamera', { namespace: 'camera' }) private loadCamera!: () => Promise<Camera[]>;
  @Action('updateCamera', { namespace: 'camera' }) private updateCameraService!: (
    cameraCreateRequest: CameraUpdateRequest
  ) => Promise<Camera[]>;
  @Action('updateConfiguration', { namespace: 'camera' }) private updateCameraConfigurationService!: (
    cameraShadow: Shadow
  ) => Promise<Shadow>;
  @Action('deleteCamera', { namespace: 'camera' }) private deleteCameraService!: (
    cameraDeleteRequest: CameraDeleteRequest
  ) => Promise<Camera[]>;

  @Getter('projectWithId', { namespace: 'projects' }) private projectWithId!: (id: string) => Project;

  private displayBatterieChart = false;
  private modalShown = false;
  private form = {
    name: '',
    serialNumber: '',
    phonenumber: '',
    serialNumberImported: false,
    serialNumberNotFound: false,
    projectId: null as string | null,
    toggleButton: false,
    detectionia: false,
    audio: false,
    safety: false,
    live: false,
    blur: false,
  };

  private shadowForm: Shadow | null = null;
  private captureDays: number[] = [];
  private isInfinite = false;
  private storeUSB = false;
  private startDate: Date | null = null;
  private endDate: Date | null = null;
  private slowMotionEnable = false;
  private slowMotionDate: Date | null = null;
  private slowMotionPeriod: Time = {};
  private isRebooting = false;
  private rebootSuccess = false;

  private isUpdatingIris = false;

  private irisVersionLoading = false;
  private irisVersionError: string | null = null;
  private irisVersion: {
    installed?: { version_name?: string; version_code?: string };
    latest?: { versionName?: string | null; versionCode?: string | null; releaseLabelId?: string | null };
    state?: 'up_to_date' | 'update_available' | 'not_installed' | 'unknown';
    repoApp?: { appId?: string; identifier?: string | null } | null;
  } = {};

  private isBootstrappingForm = true;

  private period: Period | null = null;

  private exceptPeriod: Period = {
    begin: {
      HH: undefined,
      mm: undefined,
    },
    end: {
      HH: undefined,
      mm: undefined,
    },
  };

  private intervalsOptions = [1, 2, 3, 4, 5, 10, 15, 20, 30]
    .map((x) => {
      return { text: `${x} minutes`, value: `${x}` };
    })
    .concat([
      { text: '1 heure', value: '60' },
      { text: '2 heures', value: '120' },
      { text: '6 heures', value: '360' },
      { text: '12 heures', value: '720' },
      { text: '24 heures', value: '1440' },
    ]);

  private zoomLevelOptions = [
    { text: '0.5x', value: '0.5' },
    { text: '1x', value: '1' },
    { text: '2x', value: '2' },
  ];

  private detectioniaOptions = [
    { text: 'Oui', value: true },
    { text: 'Non', value: false },
  ];

  private safetyOptions = [
    { text: 'Oui', value: true },
    { text: 'Non', value: false },
  ];

  private liveOptions = [
    { text: 'Oui', value: true },
    { text: 'Non', value: false },
  ];

  private blurOptions = [
    { text: 'Oui', value: true },
    { text: 'Non', value: false },
  ];

  private get hdrOptions() {
    return [
      { text: 'Pas de HDR', value: '0' },
      { text: 'HDR', value: '1' },
      { text: 'Automatique', value: '2' },
    ];
  }

  private static readonly AUDIO_MIN_VERSION_CODE = 49;

  get audioMinVersionCode(): number {
    return CameraModal.AUDIO_MIN_VERSION_CODE;
  }

  get installedCodeNumber(): number | null {
    const raw = this.irisVersion?.installed?.version_code;
    const n = parseInt(String(raw ?? ''), 10);
    return Number.isFinite(n) ? n : null;
  }

  get isAudioSupported(): boolean {
    const n = this.installedCodeNumber;
    return n !== null && n >= CameraModal.AUDIO_MIN_VERSION_CODE;
  }

  get audioOptions(): Array<{ text: string; value: boolean; disabled?: boolean }> {
    return [
      { text: 'Oui', value: true, disabled: !this.isAudioSupported },
      { text: 'Non', value: false },
    ];
  }

  public get datepickerLocal(): unknown {
    return this.locale === 'en' ? en : fr;
  }

  public get shadowDesynchronized(): boolean {
    if (this.camera.shadow === undefined) {
      return false;
    }
    return (
      this.camera.shadow.desired.Camera_Mode !== this.camera.shadow.reported.Camera_Mode ||
      this.camera.shadow.desired.Date_Start !== this.camera.shadow.reported.Date_Start ||
      this.camera.shadow.desired.Date_End !== this.camera.shadow.reported.Date_End ||
      this.camera.shadow.desired.Photo_Interval !== this.camera.shadow.reported.Photo_Interval ||
      this.camera.shadow.desired.Zoom_Level !== this.camera.shadow.reported.Zoom_Level ||
      this.camera.shadow.desired.Time_Start !== this.camera.shadow.reported.Time_Start ||
      this.camera.shadow.desired.Time_End !== this.camera.shadow.reported.Time_End ||
      this.camera.shadow.desired.Except_Start !== this.camera.shadow.reported.Except_Start ||
      this.camera.shadow.desired.Except_End !== this.camera.shadow.reported.Except_End ||
      this.camera.shadow.desired.Capture_Day !== this.camera.shadow.reported.Capture_Day ||
      this.camera.shadow.desired.Photo_HDRmode !== this.camera.shadow.reported.Photo_HDRmode ||
      this.camera.shadow.desired.Video_Timetrigger !== this.camera.shadow.reported.Video_Timetrigger ||
      this.camera.shadow.desired.USB_Record !== this.camera.shadow.reported.USB_Record
    );
  }

  get installedCode(): string {
    // code installé (fallback sur "—" s'il est manquant)
    return this.irisVersion?.installed?.version_code ?? '—';
  }

  get repoCode(): string {
    // code du dépôt (fallback sur "—" s'il est manquant)
    return this.irisVersion?.latest?.versionCode ?? '—';
  }

  @Validations()
  private validations = {
    startDate: { different: () => this.shadowDifferent('Date_Start') },
    endDate: { different: () => this.shadowDifferent('Date_End') },
    captureDays: { different: () => this.shadowDifferent('Capture_Day') },
    storeUSB: { different: () => this.shadowDifferent('USB_Record') },
    slowMotionPeriod: { different: () => this.shadowDifferent('Video_Timetrigger') },
    slowMotionDate: { different: () => this.shadowDifferent('Video_Timetrigger') },
    period: {
      begin: { different: () => this.shadowDifferent('Time_Start') },
      end: { different: () => this.shadowDifferent('Time_End') },
    },
    exceptPeriod: {
      begin: { different: () => this.shadowDifferent('Except_Start') },
      end: { different: () => this.shadowDifferent('Except_End') },
    },
    shadowForm: {
      Camera_Mode: { different: () => this.shadowDifferent('Camera_Mode') },
      Date_Start: { different: () => this.shadowDifferent('Date_Start') },
      Date_End: { different: () => this.shadowDifferent('Date_End') },
      Photo_Interval: { different: () => this.shadowDifferent('Photo_Interval') },
      Zoom_Level: { different: () => this.shadowDifferent('Zoom_Level') },
      Time_Start: { different: () => this.shadowDifferent('Time_Start') },
      Time_End: { different: () => this.shadowDifferent('Time_End') },
      Except_Start: { different: () => this.shadowDifferent('Except_Start') },
      Except_End: { different: () => this.shadowDifferent('Except_End') },
      Capture_Day: { different: () => this.shadowDifferent('Capture_Day') },
      Photo_HDRmode: { different: () => this.shadowDifferent('Photo_HDRmode') },
      Video_Timetrigger: { different: () => this.shadowDifferent('Video_Timetrigger') },
      USB_Record: { different: () => this.shadowDifferent('USB_Record') },
    },
  };

  public shadowDifferent(key: string): boolean {
    // @ts-ignore it's ok
    return this.camera.shadow.reported[key] !== this.camera.shadow.desired[key];
  }

  // 01102019 to Date object
  public stringToDate(str: string): Date {
    return new Date(parseInt(str.slice(4, 8), 10), parseInt(str.slice(2, 4), 10) - 1, parseInt(str.slice(0, 2), 10));
  }

  // Date object to 01102019
  public dateToString(date: Date): string {
    return ('0' + date.getDate()).slice(-2) + ('0' + (date.getMonth() + 1)).slice(-2) + date.getFullYear();
  }

  // 0110 to Period
  public stringToPeriod(str: string): Time {
    return {
      HH: str.slice(0, 2),
      mm: str.slice(2, 4),
    };
  }

  // Date object to 01102019
  public periodToString(period: Time): string {
    const mm = period.mm === '' ? '00' : period.mm;
    const hh = period.HH === '' ? '00' : period.HH;
    return hh + '' + mm;
  }

  public toggleSlowmotion(enable: boolean): void {
    if (this.shadowForm && enable === false) {
      this.shadowForm.Video_Timetrigger = '';
    } else if (this.shadowForm && this.slowMotionDate !== null) {
      this.shadowForm.Video_Timetrigger = `${this.dateToString(this.slowMotionDate)}-${this.periodToString(
        this.slowMotionPeriod
      )}`;
    }
  }

  public datepickerSlowmotion(date: Date): void {
    if (this.shadowForm && this.slowMotionPeriod.HH && this.slowMotionPeriod.mm) {
      this.shadowForm.Video_Timetrigger = `${this.dateToString(date)}-${this.periodToString(this.slowMotionPeriod)}`;
    }
  }

  public timepicketSlowmotion($event: { data: Time; displayTime: string }): void {
    logger.info($event);
    if (
      this.shadowForm &&
      this.slowMotionDate &&
      $event.displayTime !== '' &&
      $event.data.HH !== '' &&
      $event.data.mm !== ''
    ) {
      this.shadowForm.Video_Timetrigger = `${this.dateToString(this.slowMotionDate)}-${this.periodToString(
        $event.data
      )}`;
    }
  }

  public datepickerSelectedBegin(date: Date): void {
    if (this.shadowForm) {
      this.shadowForm.Date_Start = this.dateToString(date);
    }
  }

  public datepickerSelectedEnd(date: Date): void {
    if (this.shadowForm) {
      this.shadowForm.Date_End = this.dateToString(date);
    }
  }

  public daysStringtoArray(str: string): number[] {
    return str
      .split('')
      .map((d, index) => {
        let day: number = parseInt(d, 10);
        if (day === 1) {
          day = index;
        } else {
          day = -1;
        }
        return day;
      })
      .filter((day) => {
        return day !== -1;
      });
  }

  public daysArrayToString(arr: number[]): string {
    return arr
      .reduce(
        (str, day) => {
          str[day] = 1;
          return str;
        },
        [0, 0, 0, 0, 0, 0, 0]
      )
      .join('');
  }

  public get projectsOptions(): unknown[] {
    if (this.projects) {
      return [
        { text: ' -- ', value: null },
        ...this.projects
          .slice() // Crée une copie pour éviter de modifier l'original
          .sort((a: Project, b: Project) => a.name.localeCompare(b.name)) // Tri alphabétique
          .map((project: Project) => {
            return {
              text: project.name,
              value: project.id,
            };
          }),
      ];
    } else {
      return [{ text: ' -- ', value: null }];
    }
  }

  public getRotation(): string {
    return this.camera && this.camera.rotate ? `rotate(${this.camera.rotate}deg)` : '';
  }

  public mounted(): void {
    this.form.name = this.camera.name;
    this.form.serialNumber = this.camera.serialNumber;
    this.form.phonenumber = this.camera.phonenumber ?? '';
    this.form.projectId = this.camera.projectId ?? null;
    this.form.detectionia = this.camera.detectionia ?? false;
    this.form.safety = this.camera.safety ?? false;
    this.form.audio = this.camera.audio ?? false;
    this.$nextTick(() => {
      this.isBootstrappingForm = false;
    });
    this.form.live = this.camera.live ?? false;
    this.form.blur = this.camera.blur ?? false;
    if (this.camera.lastPhoto) {
      Storage.get(this.camera.lastPhoto.smallKey).then((result) => {
        this.$nextTick(() => {
          this.lastPhoto = result as string;
        });
      });
    }
    if (this.camera.shadow !== undefined) {
      this.shadowForm = Object.assign({}, this.camera.shadow.desired);
      this.captureDays = this.daysStringtoArray(this.shadowForm.Capture_Day);
      this.storeUSB = this.shadowForm.USB_Record === '1' ? true : false;
      this.startDate = this.stringToDate(this.shadowForm.Date_Start);
      this.endDate = this.stringToDate(this.shadowForm.Date_End);
      this.endDate = this.stringToDate(this.shadowForm.Date_End);
      this.period = {
        begin: this.stringToPeriod(this.shadowForm.Time_Start),
        end: this.stringToPeriod(this.shadowForm.Time_End),
      };
      this.exceptPeriod.begin = this.stringToPeriod(this.shadowForm.Except_Start);
      this.exceptPeriod.end = this.stringToPeriod(this.shadowForm.Except_End);
      if (this.shadowForm.Video_Timetrigger && this.shadowForm.Video_Timetrigger.length > 0) {
        const parsed = parse(this.shadowForm.Video_Timetrigger, 'ddMMyyy-HHmm', new Date());
        logger.info('onCameraChange', { parsed });
        this.slowMotionEnable = !isPast(parsed);
      } else {
        this.slowMotionEnable = false;
      }
    }
  }

  @Watch('captureDays')
  public updateCaptureDays(): void {
    if (this.shadowForm) {
      this.shadowForm.Capture_Day = this.daysArrayToString(this.captureDays);
    }
  }

  @Watch('isInfinite')
  public updateEndDate(): void {
    if (this.isInfinite && this.shadowForm) {
      this.endDate = new Date(2999, 11, 31);
      this.datepickerSelectedEnd(this.endDate);
    }
  }

  @Watch('storeUSB')
  public updateUSB(): void {
    if (this.shadowForm) {
      Vue.set(this.shadowForm, 'USB_Record', this.storeUSB ? '1' : '0');
    }
  }

  public updatePeriod(): void {
    if (this.shadowForm !== null && this.period !== null) {
      this.shadowForm.Time_Start = this.periodToString(this.period.begin);
      this.shadowForm.Time_End = this.periodToString(this.period.end);
    }
  }

  @Watch('exceptPeriod', { deep: true })
  public updateExceptPeriod(): void {
    if (this.shadowForm) {
      this.shadowForm.Except_Start = this.periodToString(this.exceptPeriod.begin);
      this.shadowForm.Except_End = this.periodToString(this.exceptPeriod.end);
    }
  }

  public show(): void {
    this.modal.show();
  }

  public close(): void {
    this.modal.hide();
  }

  public onModalShown(): void {
    this.modalShown = true;
    this.fetchIrisCamVersion();
  }
  public onModalHidden(): void {
    this.modalShown = false;
  }

  public onTabShown(tabIndex: number): void {
    if (tabIndex === 0) {
      this.displayBatterieChart = true;
    } else {
      this.displayBatterieChart = false;
    }
  }

  public async updateCamera(): Promise<void> {
    logger.info('updateCamera');
    if (this.camera !== undefined) {
      const cameraUpdateRequest: CameraUpdateRequest = {
        id: this.camera.id,
        name: this.form.name,
        serialNumber: this.form.serialNumber,
        phonenumber: this.form.phonenumber,
        project: this.form.projectId ? this.projectWithId(this.form.projectId) : undefined,
        rotate: this.camera.rotate !== undefined ? this.camera.rotate : 0, // Valeur par défaut, ou utilisez une valeur appropriée
        blur: this.camera.blur !== undefined ? this.camera.blur : false,
        detectionia: this.form.detectionia,
        audio: this.form.audio,
        safety: this.form.safety,
        live: this.form.live,
        remaining: this.camera.remaining !== undefined ? this.camera.remaining : 0,
      };
      return this.updateCameraService(cameraUpdateRequest)
        .then(async () => {
          if (this.shadowForm) {
            const cameraUpdateConfigurationRequest: Shadow = this.shadowForm;
            return this.updateCameraConfigurationService(cameraUpdateConfigurationRequest).then((updatedShadow) =>
              logger.info('UpdatedShadow', updatedShadow)
            );
          } else {
            return Promise.resolve();
          }
        })
        .then(() => Promise.all([this.loadProject(), this.loadCamera()]))
        .then(() => this.close());
    } else {
      this.close();
    }
  }

  private formatVersionPair(): string {
    const i = this.irisVersion?.installed?.version_name ?? '?';
    const ic = this.irisVersion?.installed?.version_code ?? '?';
    const l = this.irisVersion?.latest?.versionName ?? '?';
    const lc = this.irisVersion?.latest?.versionCode ?? '?';
    return `Installée ${i} (code ${ic}) • Dépôt ${l} (code ${lc})`;
  }

  stateBadgeVariant(): string {
    switch (this.irisVersion?.state) {
      case 'up_to_date':
        return 'success';
      case 'update_available':
        return 'warning';
      case 'not_installed':
        return 'secondary';
      default:
        return 'dark';
    }
  }

  stateLabel(): string {
    switch (this.irisVersion?.state) {
      case 'up_to_date':
        return 'À jour';
      case 'update_available':
        return 'Mise à jour disponible';
      case 'not_installed':
        return 'Non installée';
      default:
        return 'État inconnu';
    }
  }

  private async fetchIrisCamVersion(): Promise<void> {
    if (!this.camera) return;
    this.irisVersionLoading = true;
    this.irisVersionError = null;
    try {
      const resp = await cameraService.getIrisCamVersion(this.camera.id, this.camera.name);
      this.irisVersion = {
        installed: resp.installed
          ? {
              version_name: resp.installed.version_name,
              version_code: resp.installed.version_code,
            }
          : undefined,
        latest: resp.latest
          ? {
              versionName: resp.latest.versionName,
              versionCode: resp.latest.versionCode,
              // dispo si tu as ajouté releaseLabelId côté Lambda
              releaseLabelId: (resp.latest as any).releaseLabelId ?? null,
            }
          : undefined,
        state: resp.state,
        repoApp: resp.repoApp
          ? {
              appId: resp.repoApp.appId,
              identifier: resp.repoApp.identifier,
            }
          : null,
      };
      this.enforceAudioCompatibility();
    } catch (e: any) {
      console.error('getIrisCamVersion failed', e);
      this.irisVersionError = e?.message ?? 'Erreur de récupération';
    } finally {
      this.irisVersionLoading = false;
    }
  }

  private async handleUpdateIrisCam(): Promise<void> {
    if (!this.camera || this.isUpdatingIris) return;
    this.isUpdatingIris = true;

    try {
      const appId = this.irisVersion?.repoApp?.appId;
      if (!appId) throw new Error('AppId introuvable');
      const releaseLabelId = this.irisVersion?.latest?.releaseLabelId ?? null;

      const res = await cameraService.updateIrisCam(this.camera.id, {
        cameraName: this.camera.name,
        appId,
        releaseLabelId,
      });

      const httpStatus = res?.httpStatus ?? res?.statusCode ?? 202;
      const msg = httpStatus === 202 ? 'Mise à jour envoyée (queued)' : 'Demande de mise à jour envoyée';

      this.$bvToast.toast(msg, {
        title: 'Succès',
        variant: 'success',
        solid: true,
      });

      // 🔁 Rafraîchir plusieurs fois après l'envoi
      let refreshCount = 0;
      const maxRefreshes = 3; // nombre de rafraîchissements
      const refreshInterval = 20000; // délai entre chaque

      const intervalId = setInterval(async () => {
        refreshCount++;
        console.log(`🔄 Rafraîchissement #${refreshCount}`);
        await this.fetchIrisCamVersion();

        if (refreshCount >= maxRefreshes) {
          clearInterval(intervalId);
          console.log('✅ Rafraîchissements terminés');
        }
      }, refreshInterval);
    } catch (e: any) {
      console.error('updateIrisCam failed', e);
      this.$bvToast.toast(e?.message ?? 'Échec de la mise à jour', {
        title: 'Erreur',
        variant: 'danger',
        solid: true,
      });
    } finally {
      this.isUpdatingIris = false;
    }
  }

  public async handleReboot(): Promise<void> {
    if (this.isRebooting) return;

    this.isRebooting = true;
    this.rebootSuccess = false;

    try {
      // 1) on déclenche le reboot
      await cameraService.reboot(this.camera.id, this.camera.name);

      // 2) on poll le statut
      const maxAttempts = 20; // ~20s
      let attempts = 0;
      let rebootOk = false;

      while (attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, 1000)); // 1s
        const status = await cameraService.rebootStatus(this.camera.id, this.camera.name);
        console.log('Reboot status:', status);

        if (status?.status === 'success') {
          rebootOk = true;
          break;
        } else if (status?.status === 'failed') {
          throw new Error('Reboot failed');
        }
        attempts++;
      }

      // 3) UI feedback
      this.isRebooting = false;

      if (rebootOk) {
        this.rebootSuccess = true;

        // ✅ Toast succès
        const msg =
          // si tu as une clé i18n
          (this as any).$te?.('gallery.rebootSuccess')
            ? this.$t('gallery.rebootSuccess').toString()
            : 'Redémarrage réussi';
        const title = (this as any).$te?.('global.success') ? this.$t('global.success').toString() : 'Succès';

        this.$bvToast.toast(msg, {
          title,
          variant: 'success',
          solid: true,
        });

        setTimeout(() => {
          this.rebootSuccess = false;
        }, 2000);
      } else {
        throw new Error('Timeout waiting for reboot confirmation');
      }
    } catch (error) {
      console.error('Erreur lors du reboot :', error);
      this.isRebooting = false;
      this.$bvToast.toast(this.$t('gallery.rebootError').toString(), {
        title: 'Erreur',
        variant: 'danger',
        solid: true,
      });
    }
  }

  public async deleteCamera(): Promise<void> {
    logger.info('deleteCamera');
    this.$bvModal
      .msgBoxConfirm('Please confirm that you want to delete everything.', {
        title: 'Please Confirm',
        size: 'sm',
        buttonSize: 'sm',
        okVariant: 'danger',
        okTitle: 'YES',
        cancelTitle: 'NO',
        footerClass: 'p-2',
        hideHeaderClose: false,
        centered: true,
      })
      .then(async (value) => {
        logger.info('', { value });
        if (value) {
          await this.deleteCameraService({ id: this.camera.id });
          this.close();
        }
      })
      .catch((err) => {
        logger.error({ err });
      });
  }

  private enforceAudioCompatibility(): void {
    if (!this.isAudioSupported && this.form.audio === true) {
      this.form.audio = false;
    }
  }

  @Watch('form.audio')
  onAudioChange(val: boolean): void {
    if (this.isBootstrappingForm) return;
    if (this.irisVersionLoading || this.installedCodeNumber === null) return;

    if (val === true && !this.isAudioSupported) {
      this.form.audio = false;
      this.$bvToast.toast('Audio disponible à partir de la version 49.', {
        title: 'Non supporté',
        variant: 'warning',
        solid: true,
      });
    }
  }
}
